import { useState } from 'react';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useMediaSimUser } from '../../components/media-sim/layout/MediaSimUserContext';
import { usePublications, useSeedPublications, useRefreshPublicationMetadata } from '../../api/media-sim/publications';
import { PublicationsTable } from '../../components/media-sim/publications/PublicationsTable';
import { PublicationDetailPanel } from '../../components/media-sim/publications/PublicationDetailPanel';
import type { Publication } from '../../types/media-sim';

export function PublicationsPage() {
  const { theme } = useTheme();
  const { isAdmin } = useMediaSimUser();
  const { data: publications, loading, error, refetch } = usePublications({ limit: 100 });
  const { seed, loading: seeding } = useSeedPublications();
  const { refreshMetadata, loading: refreshing } = useRefreshPublicationMetadata();
  const [selectedPublication, setSelectedPublication] = useState<Publication | null>(null);

  const busy = seeding || refreshing;
  const [refreshMsg, setRefreshMsg] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);

  const handleRefresh = async () => {
    setRefreshMsg(null);
    try {
      // Seed picks up any new publications, then schedule the metadata refresh
      // (article count + date range). Both are available to any authenticated
      // user now; the refresh runs server-side as a background job.
      await seed();
      await refreshMetadata();
      setRefreshMsg({ kind: 'info', text: 'Refreshing article counts and dates… this can take a moment for large publications.' });
      // The refresh is a background job whose duration depends on collection
      // size, so re-fetch several times over ~60s rather than a fixed short
      // delay. The table re-renders reactively as populated values arrive.
      refetch();
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        refetch();
        if (attempts >= 20) {
          clearInterval(poll);
          setRefreshMsg(null);
        }
      }, 3000);
    } catch (err) {
      const e = err as { message?: string };
      setRefreshMsg({ kind: 'error', text: e?.message || 'Refresh failed. Please try again.' });
    }
  };

  const handleSelect = (pub: Publication) => {
    setSelectedPublication(selectedPublication?.id === pub.id ? null : pub);
  };

  return (
    <div style={{ padding: spacing.lg }}>
      <Stack gap="lg">
        <div>
          <Flex justify="between" align="center">
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: 600,
                color: theme.colors.text.primary,
                margin: 0,
              }}
            >
              Publications
            </h1>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={busy}
            >
              {busy ? 'Refreshing...' : 'Refresh Publications'}
            </Button>
          </Flex>
          <p
            style={{
              fontSize: '0.9rem',
              color: theme.colors.text.secondary,
              margin: `${spacing.xs} 0 0 0`,
            }}
          >
            View available publications, their editorial style profiles, and reflection status.
          </p>
          {refreshMsg && (
            <p
              style={{
                fontSize: '0.85rem',
                margin: `${spacing.xs} 0 0 0`,
                color: refreshMsg.kind === 'error'
                  ? theme.colors.semantic.error.default
                  : theme.colors.text.secondary,
              }}
            >
              {refreshMsg.text}
            </p>
          )}
        </div>

        {loading && (
          <Card variant="outlined">
            <Card.Body>
              <Stack gap="sm">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    style={{
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: theme.colors.neutral[200],
                      width: `${50 + i * 15}%`,
                    }}
                  />
                ))}
              </Stack>
            </Card.Body>
          </Card>
        )}

        {error && (
          <Card variant="outlined">
            <Card.Body>
              <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
                {error.message || 'Failed to load publications.'}
              </p>
            </Card.Body>
          </Card>
        )}

        {!loading && !error && publications && publications.length === 0 && (
          <Card variant="outlined">
            <Card.Body>
              <Stack gap="sm">
                <p style={{ color: theme.colors.text.secondary, margin: 0, fontSize: '0.95rem' }}>
                  No publications available. An administrator can seed publications.
                </p>
              </Stack>
            </Card.Body>
          </Card>
        )}

        {!loading && !error && publications && publications.length > 0 && (
          <PublicationsTable
            publications={publications}
            selectedId={selectedPublication?.id ?? null}
            onSelect={handleSelect}
          />
        )}

        {selectedPublication && (
          <PublicationDetailPanel
            publication={selectedPublication}
            isAdmin={isAdmin}
            onClose={() => setSelectedPublication(null)}
            onSaved={refetch}
          />
        )}
      </Stack>
    </div>
  );
}
